import React from "react";
import moment from "moment-timezone";
import { DollarSign, Calendar, TrendingUp, Clock, Sunrise, Sunset } from "lucide-react";
import { EmployeePerformanceCard } from "./employee-performance-card";
import { EmployeeTipBreakdown } from "./employee-tip-breakdown";

const formatMoney = (value) => {
  const numeric = parseFloat(value);
  if (!Number.isFinite(numeric)) return "0.00";
  return numeric.toFixed(2);
};

const formatHours = (value) => {
  const numeric = parseFloat(value);
  if (!Number.isFinite(numeric)) return "0";
  return Number.isInteger(numeric) ? String(numeric) : numeric.toFixed(1);
};

/**
 * Shared employee details body used by tip/cash table modals.
 * Shows tip totals, working hours (payroll), and tips by shift.
 */
export const EmployeeDetailsContent = ({ employeeDetails }) => {
  if (!employeeDetails) return null;

  const summary = employeeDetails.completeData?.summary || {};
  const tipsByShift = employeeDetails.completeData?.tips_by_shift || {};
  const morningTips = summary.morning_tips ?? tipsByShift.morning ?? 0;
  const eveningTips = summary.evening_tips ?? tipsByShift.evening ?? 0;
  const unassignedTips = summary.unassigned_shift_tips ?? tipsByShift.unassigned ?? 0;
  const workingHours = summary.working_hours ?? 0;

  return (
    <>
      <div className="mb-4 rounded-lg border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700">
        <span className="font-semibold text-slate-900">
          {employeeDetails.employee?.name || "Employee"}
        </span>
        <span className="mx-2 text-slate-400">|</span>
        <span>
          Working Hrs:{" "}
          <span className="font-semibold text-slate-900">
            {formatHours(workingHours)} hrs
          </span>
        </span>
        <span className="mx-2 text-slate-400">|</span>
        <span>
          Morning Tips (12:00 PM–5:59 PM CT):{" "}
          <span className="font-semibold text-amber-700">
            ${formatMoney(morningTips)}
          </span>
        </span>
        <span className="mx-2 text-slate-400">|</span>
        <span>
          Evening Tips (6:00 PM–5:59 AM CT):{" "}
          <span className="font-semibold text-indigo-700">
            ${formatMoney(eveningTips)}
          </span>
        </span>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <EmployeePerformanceCard
          title="Total Tips"
          value={formatMoney(summary.total_tips)}
          icon={<DollarSign className="h-4 w-4" />}
          color="blue"
        />
        <EmployeePerformanceCard
          title="Total Tax"
          value={formatMoney(summary.total_tax)}
          icon={<DollarSign className="h-4 w-4" />}
          color="blue"
        />
        <EmployeePerformanceCard
          title="Paid Out"
          value={formatMoney(summary.paid_out)}
          icon={<DollarSign className="h-4 w-4" />}
          color="blue"
        />
        <EmployeePerformanceCard
          title="Working Hrs"
          value={`${formatHours(workingHours)} hrs`}
          icon={<Clock className="h-4 w-4" />}
          color="green"
        />
        <EmployeePerformanceCard
          title="Morning Tips (12 PM–5:59 PM CT)"
          value={formatMoney(morningTips)}
          icon={<Sunrise className="h-4 w-4" />}
          color="purple"
        />
        <EmployeePerformanceCard
          title="Evening Tips (6 PM–5:59 AM CT)"
          value={formatMoney(eveningTips)}
          icon={<Sunset className="h-4 w-4" />}
          color="red"
        />
        <EmployeePerformanceCard
          title="Working Days"
          value={employeeDetails.performanceMetrics?.performance_summary?.working_days || 0}
          icon={<Calendar className="h-4 w-4" />}
          color="green"
        />
        <EmployeePerformanceCard
          title="Avg Tips/Day"
          value={formatMoney(
            employeeDetails.performanceMetrics?.performance_summary?.avg_tips_per_day
          )}
          icon={<TrendingUp className="h-4 w-4" />}
          color="purple"
        />
        {unassignedTips > 0 && (
          <EmployeePerformanceCard
            title="Unassigned Shift Tips"
            value={formatMoney(unassignedTips)}
            icon={<DollarSign className="h-4 w-4" />}
            color="blue"
          />
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800">Tip Breakdown</h3>
          </div>
          <EmployeeTipBreakdown
            data={employeeDetails.completeData?.tip_breakdown}
            role={employeeDetails?.employee?.role}
          />
        </div>

        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800">Performance Metrics</h3>
          </div>
          <div className="p-4">
            <div className="space-y-4">
              <div className="flex justify-between">
                <span className="font-medium">Best Day:</span>
                <span>
                  {employeeDetails.performanceMetrics?.daily_performance?.best_day
                    ? `${employeeDetails.performanceMetrics.daily_performance.best_day.date}: $${formatMoney(employeeDetails.performanceMetrics.daily_performance.best_day.amount)}`
                    : "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Worst Day:</span>
                <span>
                  {employeeDetails.performanceMetrics?.daily_performance?.worst_day
                    ? `${employeeDetails.performanceMetrics.daily_performance.worst_day.date}: $${formatMoney(employeeDetails.performanceMetrics.daily_performance.worst_day.amount)}`
                    : "N/A"}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Avg Tips Per Day:</span>
                <span>
                  $
                  {formatMoney(
                    employeeDetails.performanceMetrics?.performance_summary?.avg_tips_per_day
                  )}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Working Hours:</span>
                <span>{formatHours(workingHours)} hrs</span>
              </div>
              <div className="flex justify-between">
                <span className="font-medium">Morning vs Evening:</span>
                <span>
                  ${formatMoney(morningTips)} / ${formatMoney(eveningTips)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {employeeDetails.completeData?.timeline?.all_transactions?.length > 0 && (
        <div className="bg-white rounded-lg border">
          <div className="p-4 border-b bg-gray-50">
            <h3 className="text-lg font-semibold text-gray-800">Recent Transactions</h3>
          </div>
          <div className="overflow-x-auto max-h-60">
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Date
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Type
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Shift
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Tax
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Amount
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">
                    Description
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {employeeDetails.completeData.timeline.all_transactions
                  .slice(0, 10)
                  .map((transaction, index) => (
                    <tr key={index} className="hover:bg-gray-50">
                      <td className="px-4 py-3 text-sm text-gray-900">
                        {moment(transaction.date).format("MM/DD/YYYY")}
                      </td>
                      <td className="px-4 py-3 text-sm font-medium capitalize">
                        {transaction.type.replace(/_/g, " ")}
                      </td>
                      <td className="px-4 py-3 text-sm capitalize">
                        {transaction.shift ||
                          (transaction.working_hours
                            ? `${formatHours(transaction.working_hours)} hrs`
                            : "-")}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transaction.tax != null
                          ? `${transaction.tax >= 0 ? "+" : "-"}$${Math.abs(transaction.tax).toFixed(2)}`
                          : "-"}
                      </td>
                      <td className="px-4 py-3 text-sm">
                        {transaction.amount >= 0 ? "+" : "-"}$
                        {Math.abs(transaction.amount).toFixed(2)}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-600">
                        {transaction.description || "-"}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </>
  );
};

export default EmployeeDetailsContent;
